import * as React from 'react';
import { Loader2, Plus, X } from 'lucide-react';
import { uploadImage } from '../lib/uploadImage.js';
import { cn } from '../lib/utils.js';
import { Label } from './ui/label.js';

// Slides for a `gallery` button — the public proptree renders them, in
// this order, as the button's horizontal slideshow. Shared by the add-
// button modal and the expandable button row so both edit the same way.

export const MAX_GALLERY_IMAGES = 12;

export function GalleryImagesPicker({
  images,
  onChange,
  proptreeId,
}: {
  images: string[];
  onChange: (next: string[]) => void;
  // Owning proptree — tags the uploads like IconOrImagePicker does.
  proptreeId: string;
}) {
  const fileRef = React.useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = React.useState(0);
  const [error, setError] = React.useState<string | null>(null);
  const [dragFrom, setDragFrom] = React.useState<number | null>(null);
  // Uploads are async; read the list through a ref so images removed or
  // reordered mid-upload aren't resurrected when the batch lands.
  const latest = React.useRef(images);
  latest.current = images;

  const room = MAX_GALLERY_IMAGES - images.length;
  const busy = uploading > 0;

  const handleFiles = async (picked: File[]) => {
    const files = picked.slice(0, room);
    if (files.length === 0) return;
    setError(null);
    setUploading(files.length);
    const results = await Promise.allSettled(
      files.map((file) =>
        uploadImage({ file, kind: 'proptree-button', proptreeId }),
      ),
    );
    setUploading(0);
    if (fileRef.current) fileRef.current.value = '';

    // Keep every image that made it, in the order they were picked.
    const urls = results.flatMap((r) =>
      r.status === 'fulfilled' ? [r.value] : [],
    );
    if (urls.length > 0) onChange([...latest.current, ...urls]);

    const failed = results.find(
      (r): r is PromiseRejectedResult => r.status === 'rejected',
    );
    const skipped = picked.length - files.length;
    if (failed) {
      const n = results.length - urls.length;
      setError(
        `${n} of ${results.length} image${results.length === 1 ? '' : 's'} failed to upload — ${(failed.reason as Error).message}`,
      );
    } else if (skipped > 0) {
      setError(
        `A gallery holds up to ${MAX_GALLERY_IMAGES} images — ${skipped} not added.`,
      );
    }
  };

  const removeAt = (i: number) => onChange(images.filter((_, j) => j !== i));

  const moveTo = (from: number, to: number) => {
    if (from === to) return;
    const next = [...images];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved!);
    onChange(next);
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <Label>Gallery images</Label>
        <span className="font-mono text-[11px] text-muted-foreground">
          {images.length} / {MAX_GALLERY_IMAGES}
        </span>
      </div>
      <div className="mt-2 grid grid-cols-4 gap-2">
        {images.map((src, i) => (
          <div
            key={`${src}-${i}`}
            draggable={!busy}
            onDragStart={(e) => {
              e.dataTransfer.effectAllowed = 'move';
              e.dataTransfer.setData('text/plain', String(i));
              setDragFrom(i);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
            }}
            onDrop={(e) => {
              e.preventDefault();
              if (dragFrom !== null) moveTo(dragFrom, i);
              setDragFrom(null);
            }}
            onDragEnd={() => setDragFrom(null)}
            className={cn(
              'group relative aspect-square cursor-grab overflow-hidden rounded-lg border border-border bg-muted active:cursor-grabbing dark:border-white/10',
              dragFrom === i && 'opacity-50',
            )}
          >
            <img
              src={src}
              alt=""
              draggable={false}
              className="h-full w-full object-cover"
            />
            {i === 0 && (
              <span className="absolute bottom-1 left-1 rounded bg-background/85 px-1.5 py-0.5 font-sans text-[9px] font-semibold uppercase tracking-[0.06em] text-foreground">
                First
              </span>
            )}
            <button
              type="button"
              onClick={() => removeAt(i)}
              disabled={busy}
              className="absolute right-1 top-1 rounded-full bg-background/85 p-1 text-foreground opacity-0 shadow-sm transition-opacity hover:text-destructive focus-visible:opacity-100 group-hover:opacity-100"
              aria-label={`Remove image ${i + 1}`}
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
        {Array.from({ length: uploading }, (_, i) => (
          <div
            key={`uploading-${i}`}
            className="flex aspect-square items-center justify-center rounded-lg border border-dashed border-border bg-muted/40 dark:border-white/10"
          >
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ))}
        {room > 0 && !busy && (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="flex aspect-square flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-border bg-card text-muted-foreground transition-colors hover:border-foreground/30 hover:bg-muted hover:text-foreground dark:border-white/10"
            aria-label="Add images"
          >
            <Plus className="h-4 w-4" />
            <span className="font-sans text-[10px] font-semibold uppercase tracking-[0.06em]">
              Add
            </span>
          </button>
        )}
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple
        className="hidden"
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          if (files.length > 0) void handleFiles(files);
        }}
      />
      <p className="mt-2 font-sans text-[12px] text-muted-foreground">
        {images.length > 1
          ? 'Drag to reorder — the slideshow follows this order.'
          : 'Add the photos to show in this button’s slideshow.'}
      </p>
      {error && (
        <p className="mt-1 font-sans text-[12px] text-destructive">{error}</p>
      )}
    </div>
  );
}
